import { fetchUsers } from '@/lib/api/users';
import { useAuthStore } from '@/store/auth';
import { toast } from 'sonner';
import { userIdValue } from './helpers';
import { buildMeetingTaskPayload, buildUfCrmTask } from './payload';
import { CURRENT_USER_LABEL } from './types';
import type { FunnelKind, MeetingSlotFormValues } from './types';
import { useCreateMeetingTask } from './useCreateMeetingTask';
import { validateMeetingSlotSubmit } from './validation';

type UseSubmitMeetingSlotParams = {
  deadlineISO: string | null;
  funnelKind: FunnelKind;
  onClose: () => void;
  selectedDuration: 30 | 60;
  selectedEntityTypeId: number | null;
  selectedSlot: string;
  title: string;
};

export function useSubmitMeetingSlot({
  deadlineISO,
  funnelKind,
  onClose,
  selectedDuration,
  selectedEntityTypeId,
  selectedSlot,
  title,
}: UseSubmitMeetingSlotParams) {
  const { userId } = useAuthStore();
  const createMeetingTask = useCreateMeetingTask();

  const resolveResponsibleId = async (
    values: MeetingSlotFormValues
  ): Promise<number | null> => {
    if (!userId) {
      return null;
    }
    if (values.presenterName === CURRENT_USER_LABEL) {
      return userId;
    }
    if (values.presenterId) {
      return values.presenterId;
    }

    try {
      const res = await fetchUsers(values.presenterName);
      const found = res?.result?.[0];
      return userIdValue(found);
    } catch {
      return null;
    }
  };

  const submit = async (values: MeetingSlotFormValues) => {
    const ufCrmTask = buildUfCrmTask(values, funnelKind, selectedEntityTypeId);
    const validationError = validateMeetingSlotSubmit({
      deadlineISO,
      funnelKind,
      selectedSlot,
      ufCrmTask,
      userId,
      values,
    });

    if (validationError) {
      return toast.error(validationError);
    }

    const responsibleId = await resolveResponsibleId(values);
    if (!responsibleId) {
      return toast.error('Не смог определить исполнителя');
    }

    const payload = buildMeetingTaskPayload({
      deadlineISO: deadlineISO as string,
      responsibleId,
      selectedDuration,
      selectedSlot,
      title,
      ufCrmTask,
      values,
    });

    await createMeetingTask.mutateAsync({
      deadlineISO: deadlineISO as string,
      onClose,
      payload,
      selectedDuration,
    });
  };

  return {
    isSaving: createMeetingTask.isPending,
    submit,
  };
}
